/**
 * Chrome拡張(ローカルexe実行拡張)経由で、社内共有のbat/exeを起動するユーティリティ。
 *
 * 【仕組み】
 * ブラウザから直接ローカルの bat/exe は起動できないため、Chrome拡張の
 * Native Messaging を経由する。
 *
 *   Web(このコード)
 *     → chrome.runtime.sendMessage(拡張ID, {...})     ※externally_connectable
 *       → 拡張の background.js
 *         → chrome.runtime.connectNative("command")
 *           → NativeMessage.exe が Shell() でbatを起動
 *
 * 【拡張が無い環境へのフォールバック】
 * 拡張が未インストール / Chrome以外のブラウザ / 送信失敗のいずれの場合も、
 * 従来どおりパスをクリップボードにコピーする方式に自動的に切り替える。
 * これにより、拡張を配布していない利用者でも作業は継続できる。
 *
 * 【拡張IDについて】
 * 拡張の manifest.json の "key" から一意に決まる固定値。ストア非公開の
 * 自社配布拡張だが、key があるためIDは環境によらず同じになる。
 */
import { useToastStore } from '@/stores/toast'
import { copyPath } from './links'

/**
 * ローカルexe実行拡張のID。
 * manifest.json の "key"(公開鍵)から決まる固定ID。
 * 拡張を作り直して key が変わった場合はここも更新すること。
 */
const EXTENSION_ID = import.meta.env.VITE_LOCAL_EXEC_EXTENSION_ID ?? ''

/** 社内共有のバッチ置き場 */
const BAT_ROOT = '\\\\Hanami\\portal\\サポートデスク\\Data'

interface ChromeRuntimeLike {
  sendMessage: (
    extensionId: string,
    message: unknown,
    callback: (response?: { ok?: boolean; error?: string }) => void,
  ) => void
  lastError?: { message?: string }
}

function getChromeRuntime(): ChromeRuntimeLike | null {
  const c = (window as unknown as { chrome?: { runtime?: ChromeRuntimeLike } }).chrome
  // Chrome以外や拡張未インストールでは runtime.sendMessage が存在しない
  if (!c?.runtime || typeof c.runtime.sendMessage !== 'function') return null
  return c.runtime
}

/** 拡張が利用できる可能性があるか(実際に届くかは送ってみるまで分からない) */
export function isLocalExecAvailable(): boolean {
  return EXTENSION_ID !== '' && getChromeRuntime() !== null
}

/**
 * 拡張へコマンドを投げる。届かなければ false を返す。
 * 拡張が無い場合、Chromeは応答なしで lastError をセットするため、
 * それを検知してフォールバックの判断に使う。
 */
function sendToExtension(command: string): Promise<boolean> {
  const runtime = getChromeRuntime()
  if (!runtime || !EXTENSION_ID) return Promise.resolve(false)

  return new Promise((resolve) => {
    // 応答が返らないケース(拡張が無効化されている等)に備えて保険のタイムアウトを置く
    const timer = setTimeout(() => resolve(false), 1500)
    try {
      runtime.sendMessage(EXTENSION_ID, { type: 'runLocalCommand', command }, (response) => {
        clearTimeout(timer)
        if (runtime.lastError) {
          // 拡張が入っていない/IDが違う場合はここに来る
          resolve(false)
          return
        }
        resolve(response?.ok === true)
      })
    } catch {
      clearTimeout(timer)
      resolve(false)
    }
  })
}

/**
 * 関連ファイルのフォルダを開く(bat経由)。
 * 拡張が使えない環境では、従来どおりパスのコピーにフォールバックする。
 *
 * @param batName OpenProduct.bat / OpenSeller.bat
 * @param args    bat に渡す引数(スペース区切り)。
 *                OpenProduct.bat: ASIN, SellerId / OpenSeller.bat: SellerId
 */
export async function runRelatedFileBat(
  batName: 'OpenProduct.bat' | 'OpenSeller.bat',
  ...args: string[]
): Promise<void> {
  const toast = useToastStore()
  const command = `${BAT_ROOT}\\${batName} ${args.join(' ')}`

  if (await sendToExtension(command)) {
    toast.show('フォルダを開いています…')
    return
  }
  // 拡張が使えない場合は従来方式(パスをコピーして手元で実行)
  await copyPath(command)
}
